import { OrderAnalytic } from '@talos/kyoko';
import { useMemo, useState } from 'react';
import { BehaviorSubject, type Observable } from 'rxjs';
import { defaultTrajectoryZoom, type OrderTrajectoryZoom } from '../../OrderTrajectoryChart/chart';
import type { TrajectoryOrderInput } from '../../OrderTrajectoryChart/trajectoryOrder';
import type { PredictedBoundsPoint } from '../../OrderTrajectoryChart/useBoundPredictions';
import type { PreTradeBin, PreTradeResponseData, PreTradeScenarioNewOrderSingle } from '../redux/preTradeApiTypes';

/**
 * Transform PreTrade bins to OrderAnalytic format for the trajectory chart.
 * Prepends a start point from the first bin's StartTime/Start* values, then
 * appends an end point per bin using EndTime/End* values.
 */
function transformBinsToAnalytics(bins: PreTradeBin[], symbol: string): OrderAnalytic[] {
  if (!bins.length) {
    return [];
  }

  const firstBin = bins[0];
  const startPoint = new OrderAnalytic({
    Symbol: symbol,
    EndTime: firstBin.StartTime,
    UpperBound: firstBin.StartUpperBound,
    LowerBound: firstBin.StartLowerBound,
    CumQty: firstBin.StartTargetQty,
    Resolution: '1M',
  });

  return [
    startPoint,
    ...bins.map(
      bin =>
        new OrderAnalytic({
          Symbol: symbol,
          EndTime: bin.EndTime,
          UpperBound: bin.EndUpperBound,
          LowerBound: bin.EndLowerBound,
          CumQty: bin.EndTargetQty,
          Resolution: '1M',
        })
    ),
  ];
}

function parsePredictedBoundValue(value: string): number | undefined {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Transform PreTrade bins to PredictedBoundsPoint format for the prediction overlay.
 * Includes all bin Start* points plus a final terminal End* point from the last bin.
 * Invalid points are skipped to avoid plotting artificial zero or NaN jumps.
 */
function transformBinsToPredictedBoundsPoint(bins: PreTradeBin[]): PredictedBoundsPoint[] {
  if (!bins.length) {
    return [];
  }

  const predBounds: PredictedBoundsPoint[] = [];

  for (const bin of bins) {
    const target = parsePredictedBoundValue(bin.StartTargetQty);
    const lb = parsePredictedBoundValue(bin.StartLowerBound);
    const ub = parsePredictedBoundValue(bin.StartUpperBound);
    if (target == null || lb == null || ub == null) {
      continue;
    }

    predBounds.push({
      ts: new Date(bin.StartTime).getTime(),
      target,
      lb,
      ub,
    });
  }

  const lastBin = bins[bins.length - 1];
  const endTarget = parsePredictedBoundValue(lastBin.EndTargetQty);
  const endLb = parsePredictedBoundValue(lastBin.EndLowerBound);
  const endUb = parsePredictedBoundValue(lastBin.EndUpperBound);
  if (endTarget != null && endLb != null && endUb != null) {
    predBounds.push({
      ts: new Date(lastBin.EndTime).getTime(),
      target: endTarget,
      lb: endLb,
      ub: endUb,
    });
  }

  return predBounds;
}

/**
 * Create a mock observable from static analytics data.
 * Wraps the static data in a BehaviorSubject to satisfy the chart's observable interface.
 */
function createStaticAnalyticsObservable(
  analytics: OrderAnalytic[]
): Observable<{ data: Record<number, OrderAnalytic[]>; initial?: boolean }> {
  // Use legIndex 1 to match the non-multileg chart behavior
  const data: Record<number, OrderAnalytic[]> = { 1: analytics };
  return new BehaviorSubject({ data, initial: true });
}

/**
 * Create a TrajectoryOrderInput from PreTrade NewOrderSingle.
 * Only includes fields required by OrderTrajectoryChart.
 */
function createOrderFromNOS(
  nos: PreTradeScenarioNewOrderSingle,
  responseData: PreTradeResponseData
): TrajectoryOrderInput {
  const resolvedSymbol = nos.Symbol ?? responseData.Symbol;

  return {
    Symbol: resolvedSymbol,
    OrderQty: nos.OrderQty ?? responseData.Quantity,
    SubmitTime: nos.TransactTime ?? responseData.StartTime,
    Strategy: nos.Strategy ?? responseData.Strategy,
    Currency: nos.Currency ?? responseData.Currency,
    Parameters: {
      StartTime: responseData.StartTime,
      EndTime: responseData.EndTime,
      BoundsControl: responseData.BoundsControl,
    },
    Markets: nos.Markets?.map((m: PreTradeScenarioNewOrderSingle['Markets'][number]) => ({
      Market: m.Market,
      Symbol: m.Symbol ?? resolvedSymbol,
    })),
  };
}

interface UsePreTradeTrajectoryResult {
  /** Observable of analytics data for the chart */
  analyticsObservable: Observable<{ data: Record<number, OrderAnalytic[]>; initial?: boolean }>;
  /** Order input for trajectory chart from NewOrderSingle */
  order: TrajectoryOrderInput;
  /** Latest analytics point for legend display */
  latestAnalytics: OrderAnalytic | undefined;
  /** Pre-computed prediction bounds from bins */
  predBounds: PredictedBoundsPoint[];
  /** Zoom state subject */
  zoomSubject: BehaviorSubject<OrderTrajectoryZoom>;
  /** Zoom change handler */
  handleZoomChange: (zoom: Partial<OrderTrajectoryZoom>) => void;
}

/**
 * Hook that adapts PreTrade response data to the format expected by OrderTrajectoryChart.
 * Transforms static bin data into observable analytics and prediction bounds.
 */
export function usePreTradeTrajectory(scenario: PreTradeResponseData | undefined): UsePreTradeTrajectoryResult {
  const [zoomSubject] = useState(() => new BehaviorSubject<OrderTrajectoryZoom>(defaultTrajectoryZoom));

  const result = useMemo((): {
    analytics: OrderAnalytic[];
    order: TrajectoryOrderInput;
    predBounds: PredictedBoundsPoint[];
  } => {
    if (!scenario?.Bins || scenario.Bins.length === 0) {
      // Return minimal valid TrajectoryOrderInput when no scenario data
      return {
        analytics: [],
        order: { Symbol: '', OrderQty: '0', SubmitTime: '' },
        predBounds: [],
      };
    }

    const symbol = scenario.NewOrderSingle?.Symbol ?? scenario.Symbol;
    const analytics = transformBinsToAnalytics(scenario.Bins, symbol);
    const order = createOrderFromNOS(scenario.NewOrderSingle, scenario);
    const predBounds = transformBinsToPredictedBoundsPoint(scenario.Bins);

    return { analytics, order, predBounds };
  }, [scenario]);

  const analyticsObservable = useMemo(() => createStaticAnalyticsObservable(result.analytics), [result.analytics]);

  const latestAnalytics = result.analytics.at(-1);

  const handleZoomChange = useMemo(
    () => (zoom: Partial<OrderTrajectoryZoom>) => {
      const current = zoomSubject.getValue();
      zoomSubject.next({
        ...current,
        ...zoom,
      });
    },
    [zoomSubject]
  );

  return {
    analyticsObservable,
    order: result.order,
    latestAnalytics,
    predBounds: result.predBounds,
    zoomSubject,
    handleZoomChange,
  };
}
